import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, PencilLine, BookOpen, AlertCircle, CheckCircle } from 'lucide-react';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 90,
      damping: 12
    }
  }
};

export function UserProfile({
  bio,
  expectations,
  isLoading,
  isSaving,
  saveSuccess,
  error,
  hasUnsavedChanges,
  onBioChange,
  onExpectationsChange,
  onSave
}) {
  const { t } = useTranslation();

  const fields = [
    {
      id: 'bio',
      label: t('userProfile.biography.label'),
      value: bio,
      onChange: onBioChange,
      placeholder: t('userProfile.biography.placeholder'),
      icon: PencilLine,
      color: 'blue',
      borderColor: 'border-blue-200 dark:border-blue-800/40',
      focusRing: 'focus:ring-blue-500/30 focus:border-blue-500 dark:focus:border-blue-400'
    },
    {
      id: 'expectations',
      label: t('userProfile.expectations.label'),
      value: expectations,
      onChange: onExpectationsChange,
      placeholder: t('userProfile.expectations.placeholder'),
      icon: BookOpen,
      color: 'purple',
      borderColor: 'border-purple-200 dark:border-purple-800/40',
      focusRing: 'focus:ring-purple-500/30 focus:border-purple-500 dark:focus:border-purple-400'
    }
  ];

  return (
    <motion.section
      variants={container}
      initial="hidden"
      animate="show"
      className="w-full max-w-3xl mx-auto p-4 sm:p-6"
    >
      <motion.div
        variants={item}
        className="flex items-center justify-between mb-6"
      >
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
          {t('userProfile.title')}
        </h2>
        <AnimatePresence>
          {isSaving && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-full"
            >
              <Save className="w-4 h-4 animate-pulse" />
              <span>{t('userProfile.saving')}</span>
            </motion.div>
          )}
          {saveSuccess && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="flex items-center space-x-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-1.5 rounded-full"
            >
              <CheckCircle className="w-4 h-4" />
              <span>{t('userProfile.saved')}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center space-x-3 p-4 mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 text-red-700 dark:text-red-400 rounded-lg shadow-sm"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
        {fields.map((field) => {
          const Icon = field.icon;
          return (
            <motion.div
              key={field.id}
              variants={item}
              layout
              className="relative bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className={`p-2.5 rounded-full bg-${field.color}-50 dark:bg-${field.color}-900/30`}>
                  <Icon className={`w-5 h-5 text-${field.color}-600 dark:text-${field.color}-400`} />
                </div>
                <label className="block text-base font-medium text-gray-800 dark:text-gray-200">
                  {field.label}
                </label>
              </div>

              <textarea
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                disabled={isLoading}
                className={`w-full p-3 border ${field.borderColor} rounded-lg
                  bg-gray-50 dark:bg-gray-900/50 
                  text-gray-900 dark:text-white 
                  placeholder-gray-400 dark:placeholder-gray-500
                  ${field.focusRing}
                  disabled:bg-gray-100 dark:disabled:bg-gray-800 
                  disabled:text-gray-500 dark:disabled:text-gray-400
                  resize-none transition-all duration-200 
                  text-sm md:text-base leading-relaxed
                  focus:shadow-inner`}
                placeholder={field.placeholder}
                rows={4}
              />

              {isLoading && field.value && (
                <div className="absolute top-4 right-4">
                  <div className="animate-pulse flex space-x-1">
                    <div className="h-2 w-2 bg-blue-400 rounded-full"></div>
                    <div className="h-2 w-2 bg-blue-400 rounded-full delay-75"></div>
                    <div className="h-2 w-2 bg-blue-400 rounded-full delay-150"></div>
                  </div>
                </div>
              )}

              <div className="mt-2 text-xs text-right text-gray-400 dark:text-gray-500">
                {field.value.length > 0 ? field.value.length : 0} {t('userProfile.characters')}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Save Button */}
      <motion.div
        variants={item}
        className="mt-6 flex items-center justify-center"
      >
        <button
          onClick={onSave}
          disabled={isLoading || !hasUnsavedChanges}
          className={`flex items-center px-5 py-2.5 rounded-lg font-medium text-white 
            transition-all duration-200 shadow-sm
            ${hasUnsavedChanges
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-400 cursor-not-allowed'
            }`}
        >
          <Save className="w-4 h-4 mr-2" />
          {t('userProfile.saveChanges')}
        </button>
      </motion.div>

      <motion.div
        variants={item}
        className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400"
      >
        {hasUnsavedChanges
          ? t('userProfile.unsavedChanges')
          : t('userProfile.allChangesSaved')}
      </motion.div>
    </motion.section>
  );
}

export default UserProfile;